import { Component, OnInit } from '@angular/core'
import {
    ProspectScrapeRun,
    ProspectScrapeRunStatusLabels,
    ProspectScrapeJob,
    ProspectScrapeJobStatusLabels,
    ProspectSearchTerm,
    ProspectArea as ProspectAreaModel,
} from '@parkupp/core'
import { ProspectingService, ProspectArea } from 'src/app/services/prospecting.service'

const COST_PER_PLACE_USD = 0.0021

@Component({
    selector: 'app-admin-prospecting-scraper',
    templateUrl: './admin-prospecting-scraper.component.html',
    styleUrls: ['./admin-prospecting-scraper.component.scss'],
})
export class AdminProspectingScraperComponent implements OnInit {
    activeTab: 'quick' | 'jobs' = 'quick'

    // Quick scrape
    areas: ProspectArea[] = []
    defaultSearchTerms: string[] = []
    selectedArea = ''
    searchTerm = 'head office'
    maxResults = 50
    runs: ProspectScrapeRun[] = []
    runStatusLabels = ProspectScrapeRunStatusLabels

    starting = false
    loadingConfig = true
    message = ''
    error = ''
    status: any = null
    loadingStatus = false

    // Search terms (new pipeline)
    configuredTerms: ProspectSearchTerm[] = []
    newTermName = ''
    newTermQuery = ''

    // Areas (new pipeline)
    configuredAreas: ProspectAreaModel[] = []

    // Jobs (new pipeline)
    scrapeJobs: ProspectScrapeJob[] = []
    jobStatusLabels = ProspectScrapeJobStatusLabels
    enqueueing = false

    constructor(private prospecting: ProspectingService) {}

    async ngOnInit(): Promise<void> {
        try {
            const config = await this.prospecting.getConfig()
            this.areas = config.areas
            this.defaultSearchTerms = config.defaultSearchTerms
        } catch (e: any) {
            this.error = e?.message || 'Failed to load scraper config'
        } finally {
            this.loadingConfig = false
        }

        this.loadData()
    }

    async loadData(): Promise<void> {
        try {
            const [runs, terms, areas, jobs] = await Promise.all([
                this.prospecting.listScrapeRuns(),
                this.prospecting.listSearchTerms(),
                this.prospecting.listAreas(),
                this.prospecting.listScrapeJobs(),
            ])
            this.runs = runs
            this.configuredTerms = terms
            this.configuredAreas = areas
            this.scrapeJobs = jobs
        } catch (e) {
            console.error('Error loading scraper data:', e)
        }
    }

    // ---------- Quick scrape ----------

    get cappedMax(): number {
        return Math.max(1, Math.min(this.maxResults || 0, 9999))
    }

    get estimatedCost(): number {
        return Math.round(this.cappedMax * COST_PER_PLACE_USD * 100) / 100
    }

    async runScrape(): Promise<void> {
        this.message = ''
        this.error = ''
        if (!this.selectedArea) {
            this.error = 'Please choose an area'
            return
        }
        const term = this.searchTerm.trim()
        if (!term) {
            this.error = 'Please enter a search term'
            return
        }
        this.starting = true
        try {
            const result = await this.prospecting.startMapsScrape(this.selectedArea, [term], this.cappedMax)
            const count = result?.runs?.length || 0
            this.message = `Started ${count} scrape run(s) for "${term}" with up to ${this.cappedMax} results.`
        } catch (e: any) {
            this.error = e?.message || 'Failed to start scrape'
        } finally {
            this.starting = false
        }
    }

    async loadStatus(): Promise<void> {
        this.loadingStatus = true
        try {
            this.status = await this.prospecting.getStatus()
        } catch (e: any) {
            this.error = e?.message || 'Failed to load account status'
        } finally {
            this.loadingStatus = false
        }
    }

    areaName(slug?: string): string {
        return this.areas.find((a) => a.slug === slug)?.name || slug || '-'
    }

    runDate(run: any): Date | null {
        const ts: any = run.createdAt
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        return new Date(ts)
    }

    // ---------- Search Terms ----------

    async addConfiguredTerm(): Promise<void> {
        const name = this.newTermName.trim()
        const q = this.newTermQuery.trim() || name.toLowerCase()
        if (!name) return
        try {
            await this.prospecting.addSearchTerm(name, q)
            this.newTermName = ''
            this.newTermQuery = ''
        } catch (e: any) {
            this.error = e?.message || 'Failed to add search term'
        }
    }

    async toggleTerm(term: ProspectSearchTerm): Promise<void> {
        if (!term.$key) return
        await this.prospecting.updateSearchTerm(term.$key, { enabled: !term.enabled })
    }

    async deleteTerm(term: ProspectSearchTerm): Promise<void> {
        if (!term.$key || !confirm(`Delete "${term.name}"?`)) return
        await this.prospecting.deleteSearchTerm(term.$key)
    }

    // ---------- Areas (hardcoded from config) ----------

    get groupedHardcodedAreas(): { region: string; areas: ProspectArea[] }[] {
        const map = new Map<string, ProspectArea[]>()
        for (const a of this.areas) {
            if (!map.has(a.region)) map.set(a.region, [])
            map.get(a.region)!.push(a)
        }
        return Array.from(map.entries()).map(([region, areas]) => ({ region, areas }))
    }

    // ---------- Jobs ----------

    async enqueueJobs(): Promise<void> {
        this.enqueueing = true
        this.message = ''
        this.error = ''
        try {
            const result = await this.prospecting.enqueueScrapeJobs()
            this.message = `Created ${result.created} job(s), skipped ${result.skipped}.`
        } catch (e: any) {
            this.error = e?.message || 'Failed to enqueue jobs'
        } finally {
            this.enqueueing = false
        }
    }

}
